import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, IconButton, Dialog } from '@mui/material';
import { Visibility, VisibilityOff, Close } from '@material-ui/icons';
import { useFormik } from 'formik';
import useStyles from '../../assets/styles/DQProV2SignupStyles';
import PoweredByDQ_Default from '../../assets/images/PoweredByDQ_Default.png';
import PoweredByDQ_White from '../../assets/images/PoweredByDQ_white.png';
import { useColorMode } from '../../context/colorMode/colorMode.context';
import { changePassword } from '../../services/auth.service';
import { toast } from '../../utils/utils';
import { TOAST_TYPES } from '../../utils/constants';
import { useAuth } from '../../context/auth';
import LoaderWithBackDrop from '../LoaderWithBackDrop/LoaderWithBackDrop.comp';
import PasswordWithValidation, { getPasswordChecks } from '../../pages/DQPro/DQProV2/components/PasswordWithValidationsField.comp';


/**
 * Modal change-password flow (e.g. DQ Pro program preview v2) — keeps user on the current page.
 * Styled to match the other DQProV2 auth dialogs on this page (SignupDialogV2, GuestLoginDialogV2).
 */
const ChangePasswordDialog = ({ open, onClose }) => {
    const classes = useStyles();
    const { isDark } = useColorMode();
    const PoweredByDQ_Logo = isDark ? PoweredByDQ_White : PoweredByDQ_Default;
    const { authUser, setAuthUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({ new_password: '', confirm_new_password: '' });
    const [showOldPassword, setShowOldPassword] = useState(false);

    const formik = useFormik({
        initialValues: {
            old_password: '',
            new_password: '',
            confirm_new_password: '',
        },
        enableReinitialize: true,
        onSubmit: async (values) => {
            const newErrors = { new_password: '', confirm_new_password: '' };

            const checks = getPasswordChecks(values.new_password);
            if (!Object.values(checks).every(Boolean)) {
                newErrors.new_password = 'Password must include 8+ chars, upper, lower, number, and special character.';
            } else if (values.new_password === values.old_password) {
                newErrors.new_password = 'New password must be different from your old password.';
            }

            if (values.new_password !== values.confirm_new_password) {
                newErrors.confirm_new_password = "The Confirm Password confirmation doesnt match.";
            }

            setErrors(newErrors);
            if (newErrors.new_password || newErrors.confirm_new_password) return;

            setLoading(true);
            try {
                const result = await changePassword(values.old_password, values.new_password);
                if (result.data?.updated_password) {
                    setAuthUser({
                        ...authUser,
                        required_password_reset: false,
                    });
                    toast(TOAST_TYPES.SUCCESS, 'Password Changed', 'Successfully changed password');
                    formik.resetForm();
                    onClose();
                }
            } catch (_) {
                // invokeApi already surfaces errors
            } finally {
                setLoading(false);
            }
        },
    });

    useEffect(() => {
        if (!open) {
            formik.resetForm();
            setErrors({ new_password: '', confirm_new_password: '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleDialogClose = () => {
        if (loading) return;
        formik.resetForm();
        setErrors({ new_password: '', confirm_new_password: '' });
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: '24px',
                    padding: '20px 0px',
                    maxWidth: '480px',
                    overflowX: 'hidden',
                    backgroundColor: 'var(--brand-surface, #fff)',
                },
            }}
        >
            <IconButton onClick={handleDialogClose} disabled={loading} style={{ position: 'absolute', right: 10, top: 10, color: 'var(--brand-text-muted, #999)' }}>
                <Close />
            </IconButton>
            <Box style={{ padding: '0 40px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography
                    sx={{
                        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                        background: 'linear-gradient(to right, #6A8EC9 10%, #8C74A7 35%, #D44C71 59%, #C91C6E 100%)',
                        color: 'transparent',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontWeight: 500,
                        textAlign: 'center',
                        fontSize: '32px',
                        mb: '10px',
                    }}
                >
                    Change Password
                </Typography>
                <img src={PoweredByDQ_Logo} alt="Powered by DQ" className={classes.logo} style={{ height: '70px', marginBottom: '25px' }} />

                <form className={classes.form} onSubmit={formik.handleSubmit}>
                    <Typography className={classes.label}>Current Password</Typography>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                        <input
                            className={classes.input}
                            type={showOldPassword ? 'text' : 'password'}
                            name="old_password"
                            autoComplete="current-password"
                            placeholder="Enter current password"
                            value={formik.values.old_password}
                            onChange={formik.handleChange}
                            disabled={loading}
                            style={{ paddingRight: '42px' }}
                        />
                        <IconButton
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setShowOldPassword((prev) => !prev)}
                            disabled={loading}
                            sx={{ position: 'absolute', right: 8, top: '40%', transform: 'translateY(-50%)' }}
                        >
                            {showOldPassword ? <VisibilityOff style={{ fontSize: 16 }} /> : <Visibility style={{ fontSize: 16 }} />}
                        </IconButton>
                    </Box>

                    <Typography className={classes.label}>New Password</Typography>
                    <PasswordWithValidation
                        value={formik.values.new_password}
                        onChange={(val) => formik.setFieldValue('new_password', val)}
                        placeholder="Create new password"
                        name="new_password"
                        disabled={loading}
                        inputClassName={classes.input}
                    />
                    {errors.new_password && (
                        <Typography style={{ color: 'red', fontSize: '12px', marginTop: '-10px', marginBottom: '10px' }}>
                            {errors.new_password}
                        </Typography>
                    )}

                    <Typography className={classes.label}>Confirm new password</Typography>
                    <PasswordWithValidation
                        value={formik.values.confirm_new_password}
                        onChange={(val) => formik.setFieldValue('confirm_new_password', val)}
                        placeholder="Confirm new password"
                        name="confirm_new_password"
                        disabled={loading}
                        inputClassName={classes.input}
                    />
                    {errors.confirm_new_password && (
                        <Typography style={{ color: 'red', fontSize: '12px', marginTop: '-10px', marginBottom: '10px' }}>
                            {errors.confirm_new_password}
                        </Typography>
                    )}

                    <Button className={classes.continueButton} type="submit" disabled={loading}>
                        {loading ? 'Please wait...' : 'Change Password'}
                    </Button>
                </form>
            </Box>
            <LoaderWithBackDrop loading={loading} />
        </Dialog>
    );
};

export default ChangePasswordDialog;
